import {signalStore, withState, withMethods, patchState, withComputed} from '@ngrx/signals';
import {catchError, forkJoin, map, Observable, of, tap, throwError} from 'rxjs';
import {computed, inject} from '@angular/core';
import {IVendor} from '../../types/IVendor';
import {IAvailabilitySource} from '../../types/IAvailabilitySource';
import {VendorsService} from '../services/vendors.service';
import {IFailedOrder, ISubmittedOrder, OrderFields, OrdersService} from '../services/orders.service';
import {ToastService} from '../services/toast.service';
import {setError, setFulfilled, setIdle, setPending, withRequestStatus} from './features/with-request-status';

type OrderState = {
  vendors: Record<string, IVendor>;
  activeVendorIndex: number;
  orderFields: OrderFields;
  // After a partial failure only the failed vendors' forms are shown again
  retryVendorKeys: string[];
  submittedVendorKeys: string[];
  // Shown on the order form, not just the last request's error: it lists every vendor that failed
  // a partial submit and stays visible while the user steps through the retry forms
  error: string | null;
};

export type OrderSubmitResult = {
  submitted: ISubmittedOrder[];
  failed: IFailedOrder[];
};

export const OrderStore = signalStore(
  {providedIn: 'root'},
  withState<OrderState>({
    vendors: {},
    activeVendorIndex: 0,
    orderFields: {},
    retryVendorKeys: [],
    submittedVendorKeys: [],
    error: null,
  }),
  withRequestStatus('submit'),
  withComputed((store) => {
    // Only vendors with order fields need a form, the rest are submitted with the client's defaults
    const formVendors = computed(() =>
      Object.values(store.vendors())
        .filter(vendor => Object.keys(vendor.order_fields?.properties ?? {}).length)
        .filter(vendor => !store.retryVendorKeys().length || store.retryVendorKeys().includes(vendor.key))
    );

    return {
      formVendors,
      hasSubmittedOrders: computed(() => store.submittedVendorKeys().length > 0),
      activeVendor: computed(() => formVendors()[store.activeVendorIndex()] ?? null),
      isLastVendor: computed(() => store.activeVendorIndex() >= formVendors().length - 1),
    };
  }),
  withMethods((store, vendorsService = inject(VendorsService), ordersService = inject(OrdersService), toastService = inject(ToastService)) => {
    const vendorName = (key: string) => store.vendors()[key]?.name ?? key;

    const notifySubmitted = (orders: ISubmittedOrder[]) =>
      [...new Set(orders.filter(order => !order.previously_submitted).map(order => order.vendor))]
        .forEach(key => toastService.success(`Successfully submitted order to ${vendorName(key)}`));

    return {
      loadVendorsForSources(sources: IAvailabilitySource[]): Observable<Record<string, IVendor>> {
        patchState(store, {vendors: {}, activeVendorIndex: 0, orderFields: {}, retryVendorKeys: [], submittedVendorKeys: [], error: null});

        const distributors = sources.filter(s => s.type === 'distributor');
        if (distributors.length === 0) return of({});

        const vendorKeys = [...new Set(distributors.map(s => s.vendor.key))];
        const requestMap = vendorKeys.reduce((acc, key) => {
          acc[key] = vendorsService.getVendor(key);
          return acc;
        }, {} as Record<string, Observable<IVendor>>);

        return forkJoin(requestMap).pipe(
          tap((vendors) => patchState(store, {vendors})),
          catchError((error) => {
            patchState(store, {error: error.message});
            return throwError(() => error);
          })
        );
      },

      saveVendorFields(vendorKey: string, values: Record<string, any>) {
        patchState(store, {
          orderFields: {...store.orderFields(), [vendorKey]: values},
          activeVendorIndex: store.activeVendorIndex() + 1,
          // While retrying the error lists every failed vendor, so keep it visible on the next form
          error: store.retryVendorKeys().length ? store.error() : null,
        });
      },

      previousVendor() {
        patchState(store, {activeVendorIndex: Math.max(0, store.activeVendorIndex() - 1), error: null});
      },

      // Reopens a vendor's form (or the last one) so the user can fix what the API rejected
      editVendor(vendorKey?: string) {
        const index = store.formVendors().findIndex(vendor => vendor.key === vendorKey);
        patchState(store, {activeVendorIndex: index >= 0 ? index : store.formVendors().length - 1});
      },

      // Reopens the forms of the vendors that failed so their fields can be changed before retrying.
      // Returns false when none of them have a form to edit.
      editFailedVendors(failed: IFailedOrder[]): boolean {
        const keys = failed.map(order => order.vendor)
          .filter(key => Object.keys(store.vendors()[key]?.order_fields?.properties ?? {}).length);

        if (!keys.length) return false;

        patchState(store, {retryVendorKeys: [...new Set(keys)], activeVendorIndex: 0});
        return true;
      },

      // Submits every vendor order for the cart in one request. Safe to call again after a partial
      // failure since the API skips orders that already went through.
      submitCart(): Observable<OrderSubmitResult> {
        patchState(store, setPending('submit'), {error: null});

        return ordersService.submitCart(store.orderFields()).pipe(
          map((submitted): OrderSubmitResult => ({submitted: submitted ?? [], failed: []})),
          catchError((err) => {
            if (!err.errors?.failed) {
              return throwError(() => err);
            }

            return of<OrderSubmitResult>({submitted: err.errors.submitted ?? [], failed: err.errors.failed});
          }),
          tap(({submitted, failed}) => {
            notifySubmitted(submitted);
            patchState(store, setFulfilled('submit'), {
              submittedVendorKeys: [...new Set([...store.submittedVendorKeys(), ...submitted.map(order => order.vendor)])],
              error: failed.length
                ? failed.map(order => `${vendorName(order.vendor)}: ${order.errors?.message ?? 'Unable to submit order'}`).join('; ')
                : null,
            });
          }),
          catchError((err) => {
            const fields: string[] = err.errors?.fields ?? [];
            patchState(store, setError('submit', err.message), {error: fields.length ? `${err.message}: ${fields.join(', ')}` : err.message});
            return throwError(() => err);
          }),
        );
      },

      reset: () => patchState(store, setIdle('submit'), {vendors: {}, activeVendorIndex: 0, orderFields: {}, retryVendorKeys: [], submittedVendorKeys: [], error: null}),
    };
  })
);
